import React from 'react';
import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Select, SelectItem } from '@heroui/react';
import { toast } from 'react-toastify';

import type { ExperimentItem } from '@/types';
import {
  getAllExperimentStatuses,
  getStatusDescription,
  getStatusLabel,
  type ExperimentStatus
} from '@/utils/experiments';

interface SetExperimentStatusModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  experiment: ExperimentItem;
  onSubmit: (status: ExperimentStatus) => Promise<void>;
}

export function SetExperimentStatusModal({
  isOpen,
  onOpenChange,
  experiment,
  onSubmit
}: SetExperimentStatusModalProps) {
  const [selectedStatus, setSelectedStatus] = React.useState<ExperimentStatus>(experiment.status);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // Reset status when modal opens or experiment changes
  React.useEffect(() => {
    if (isOpen && experiment) {
      setSelectedStatus(experiment.status);
    }
  }, [isOpen, experiment]);

  const handleSubmit = React.useCallback(async () => {
    try {
      setIsSubmitting(true);
      await onSubmit(selectedStatus);
      toast.success(`Experiment status updated to ${selectedStatus}`);
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to update experiment status:', error);
      // Error is handled by axios interceptor
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedStatus, onSubmit, onOpenChange]);

  const handleClose = React.useCallback(() => {
    setSelectedStatus(experiment.status);
    onOpenChange(false);
  }, [onOpenChange, experiment.status]);

  return (
    <Modal isOpen={isOpen} placement="center" onOpenChange={handleClose} size="md" isDismissable={false}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">Change Experiment Status</ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-4">
                <p className="text-default-600 text-sm">
                  Select the status for <span className="font-semibold">{experiment.name}</span>
                </p>

                <Select
                  label="Status"
                  placeholder="Select a status"
                  selectedKeys={[selectedStatus]}
                  onSelectionChange={(keys) => {
                    const selected = Array.from(keys)[0] as ExperimentStatus;
                    setSelectedStatus(selected);
                  }}
                  variant="bordered"
                >
                  {getAllExperimentStatuses()
                    .filter((status) => ['active', 'draft', 'running'].includes(status))
                    .map((status) => (
                      <SelectItem key={status} description={getStatusDescription(status)}>
                        {getStatusLabel(status)}
                      </SelectItem>
                    ))}
                </Select>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={onClose} isDisabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                color="primary"
                isLoading={isSubmitting}
                onPress={handleSubmit}
                isDisabled={selectedStatus === experiment.status}
              >
                Update Status
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
